import api from '@/lib/api';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
const getProductNames = (d) => {
  if (!d.items || d.items.length === 0) return "—";
  return d.items.map(it => it.productName || it.name || "Item").join(", ");
};

const getRequestedQty = (d) => {
  if (!d.items || d.items.length === 0) return d.totalQty ?? 0;
  return d.items.reduce((sum, it) => sum + (Number(it.requestedQuantity ?? it.quantity ?? 0)), 0);
};


import { Link, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Send, Check, X, Search, PlusCircle, Trash2, Receipt, IndianRupee, Sparkles, CheckSquare, Square } from "lucide-react";
import API from "@/lib/api";

const STATUS_TABS = ["All", "Pending", "Approved", "Dispatched", "Rejected", "Cancelled"];

function formatDate(val) {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return String(val);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function StatusBadge({ status }) {
  const s = (status || "PENDING").toUpperCase();
  let color = "bg-zinc-100 text-zinc-700 border-zinc-200";

  if (s === "PENDING") color = "bg-amber-50 text-amber-700 border-amber-200";
  else if (s === "APPROVED") color = "bg-blue-50 text-blue-700 border-blue-200";
  else if (s === "DISPATCHED") color = "bg-zinc-100 text-zinc-800 border-zinc-300";
  else if (s === "COMPLETED") color = "bg-emerald-50 text-emerald-700 border-emerald-200";
  else if (s === "REJECTED" || s === "CANCELLED") color = "bg-rose-50 text-rose-700 border-rose-200";

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wider border ${color}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {s}
    </span>
  );
}

export function DispatchList() {
  const [activeTab, setActiveTab] = useState("Pending");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [appliedDateRange, setAppliedDateRange] = useState({ from: "", to: "" });
  const [selected, setSelected] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");

  const { data: dispatches = [], isLoading } = useQuery({
    queryKey: ["dispatch-requests"],
    queryFn: () => API.get("/dispatch-requests").then((r) => r.data?.data || r.data || []),
  });

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: () => API.get("/warehouse/customers").then((r) => r.data?.data || r.data || []),
  });

  // Robust customer list built from both customers query and dispatches
  const customerOptions = useMemo(() => {
    const map = new Map();
    (customers || []).forEach((c) => {
      const id = c.id || c._id;
      const name = c.businessName || c.name || c.profile?.businessName;
      if (id && name) map.set(id, name);
    });
    (dispatches || []).forEach((d) => {
      const cId = d.customerId || d.customer?.id || d.customer?._id;
      const cName = d.customer?.businessName || d.customerName || d.customer?.name;
      if (cId && cName && !map.has(cId)) {
        map.set(cId, cName);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [customers, dispatches]);

  const filteredItems = useMemo(() => {
    return (dispatches || []).filter((d) => {
      // 1. Customer dropdown filter
      if (selectedCustomerId) {
        const dCustId = d.customerId || d.customer?.id || d.customer?._id;
        if (dCustId !== selectedCustomerId) return false;
      }

      // 2. Search query filter
      if (searchTerm && searchTerm.trim()) {
        const q = searchTerm.toLowerCase().trim();
        const dId = String(d.businessId || d.dispatchId || d.id || "").toLowerCase();
        const custName = String(d.customer?.businessName || d.customerName || "").toLowerCase();
        
        const itemMatches = Array.isArray(d.items) && d.items.some((i) => {
          const pName = String(i.productName || i.inventory?.productName || "").toLowerCase();
          const bNum = String(i.batchNumber || i.inventory?.batchNumber || "").toLowerCase();
          const sId = String(i.inventory?.businessId || i.inventoryBusinessId || "").toLowerCase();
          return pName.includes(q) || bNum.includes(q) || sId.includes(q);
        });

        
        const directInv = (d.invoiceNumber || d.invoice?.invoiceNumber || d.dispatchInvoiceNumber || "").toLowerCase();
        const invMatches = directInv.includes(q);

        if (!dId.includes(q) && !custName.includes(q) && !itemMatches && !invMatches) {
          return false;
        }
      }

      // 3. Status tab filter
      if (activeTab && activeTab.toLowerCase() !== "all") {
        if ((d.status || "").toLowerCase() !== activeTab.toLowerCase()) return false;
      }

      // 4. Date range filter
      if (appliedDateRange.from) {
        const itemDate = new Date(d.requestedAt || d.createdAt || d.dispatchedAt || d.dispatchDate);
        if (itemDate < new Date(appliedDateRange.from)) return false;
      }
      if (appliedDateRange.to) {
        const itemDate = new Date(d.requestedAt || d.createdAt || d.dispatchedAt || d.dispatchDate);
        const toEnd = new Date(appliedDateRange.to);
        toEnd.setHours(23, 59, 59, 999);
        if (itemDate > toEnd) return false;
      }

      return true;
    });
  }, [dispatches, searchTerm, selectedCustomerId, activeTab, appliedDateRange, customers]);

  const allSelected = filteredItems.length > 0 && filteredItems.every((d) => selected[d.id]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected({});
    } else {
      const next = {};
      filteredItems.forEach((d) => { next[d.id] = true; });
      setSelected(next);
    }
  };

  const handleApplyDate = () => {
    setAppliedDateRange({ from: dateFrom, to: dateTo });
  };

  const handleClearDate = () => {
    setDateFrom("");
    setDateTo("");
    setAppliedDateRange({ from: "", to: "" });
  };

  
  // Dynamic counts for status filter tabs
  const getStatusCount = (statusKey) => {
    const list = Array.isArray(items) ? items : (Array.isArray(dispatchRequests) ? dispatchRequests : []);
    if (!statusKey || statusKey === "ALL") return list.length;
    return list.filter((item) => (item.status || "").toUpperCase() === statusKey.toUpperCase()).length;
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold text-zinc-950 tracking-tight">Dispatch requests</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Approve, reject, or dispatch customer requests. Same-day dispatches for one customer are grouped into a single invoice.
        </p>
      </div>

      {/* Search and Customer Dropdown Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
        <div className="relative">
          <input
            type="text"
            placeholder="Search Dispatch ID, Invoice #, customer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-9 pl-9 pr-8 rounded-lg border border-zinc-200 bg-white text-xs font-mono text-zinc-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 placeholder:text-zinc-400 font-sans"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-400">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-zinc-400 hover:text-zinc-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div>
          <select
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(e.target.value)}
            className="w-full h-9 px-3 rounded-lg border border-zinc-200 bg-white text-xs text-zinc-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
          >
            <option value="">All Customers</option>
            {customerOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Filter Row: Tabs & Date Pickers */}
      <div className="flex flex-wrap items-end justify-between gap-4 pt-1 pb-1">
        {/* Status Pills */}
        <div className="flex items-center gap-1 bg-zinc-100/80 p-1 rounded-xl border border-zinc-200">
          {STATUS_TABS.map((tab) => {
            const tabKey = tab.toUpperCase();
            const rawList = (typeof dispatches !== "undefined" && Array.isArray(dispatches)) ? dispatches
              : (typeof dispatchRequests !== "undefined" && Array.isArray(dispatchRequests)) ? dispatchRequests
              : (typeof data !== "undefined" && Array.isArray(data)) ? data
              : (typeof filteredItems !== "undefined" && Array.isArray(filteredItems)) ? filteredItems
              : [];
            const count = tabKey === "ALL"
              ? rawList.length
              : rawList.filter((item) => (item.status || "").toUpperCase() === tabKey).length;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  activeTab === tab
                    ? "bg-zinc-950 text-white shadow-sm"
                    : "text-zinc-600 hover:text-zinc-950 hover:bg-zinc-200/60"
                }`}
              >
                {tab} ({count})
              </button>
            );
          })}
        </div>

        {/* Date Filter Controls */}
        <div className="flex items-center gap-3">
          <div>
            <label className="block text-[10px] font-mono tracking-wider text-zinc-400 uppercase mb-1">
              DATE FROM
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-9 px-3 rounded-lg border border-zinc-200 bg-white text-xs font-mono text-zinc-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
            />
          </div>

          <div>
            <label className="block text-[10px] font-mono tracking-wider text-zinc-400 uppercase mb-1">
              DATE TO
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-9 px-3 rounded-lg border border-zinc-200 bg-white text-xs font-mono text-zinc-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
            />
          </div>

          <div className="flex items-center gap-1.5 pt-4">
            <button
              onClick={handleApplyDate}
              className="h-9 px-4 rounded-lg bg-zinc-950 text-white text-xs font-medium hover:bg-zinc-800 shadow-sm transition-all"
            >
              Apply
            </button>
            {(dateFrom || dateTo || appliedDateRange.from || appliedDateRange.to) && (
              <button
                onClick={handleClearDate}
                className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-500 hover:text-zinc-900 shadow-sm transition-all"
                title="Clear Dates"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs whitespace-nowrap">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50/50 text-[11px] font-mono tracking-wider text-zinc-500 uppercase">
                <th className="px-4 py-3.5 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="rounded border-zinc-300 text-zinc-950 focus:ring-zinc-950"
                  />
                </th>
                <th className="px-4 py-3.5">DISPATCH ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider">Invoice #</th>
                
                <th className="px-4 py-3.5">CUSTOMER</th>
                <th className="px-4 py-3.5">PRODUCT NAME & QTY</th>
                <th className="px-4 py-3.5">REQUESTED DATE</th>
                <th className="px-4 py-3.5">EXPECTED DISPATCH</th>
                <th className="px-4 py-3.5">DISPATCH DATE</th>
                <th className="px-4 py-3.5">STATUS</th>
                <th className="px-4 py-3.5 text-right">VIEW</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {isLoading ? (
                <tr>
                  <td colSpan="10" className="px-4 py-12 text-center text-zinc-400">
                    Loading dispatch requests...
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-4 py-12 text-center text-zinc-400">
                    No dispatch requests found.
                  </td>
                </tr>
              ) : (
                filteredItems.map((d) => {
                  const cust = customers.find((c) => c.id === d.customerId);
                  const custName = d.customer?.businessName || cust?.businessName || d.customerName || "Relaince Jio";
                  const mergedProductDisplay = Array.isArray(d.items) && d.items.length > 0
    ? d.items.map((i) => `${i.productName || i.product_name || "Item"} (${i.requestedQuantity || i.requested_quantity || 0} QTY)`).join(", ")
    : "—";
                  const dispatchInvNo = d.dispatchInvoiceNumber || d.dispatchInvNo || (d.dispatchNumber ? `INV-${d.businessId || d.dispatchId || d.dispatchNumber || (d.id ? `DSP-${d.id.slice(0, 6)}` : "—")}` : null);
                  const invoiceNo = d.invoiceNumber || d.invoice?.invoiceNumber || d.invoiceNo || null;

                  return (
                    <tr key={d.id} className="hover:bg-zinc-50/80 transition-colors">
                      <td className="px-4 py-3.5">
                        <input
                          type="checkbox"
                          checked={!!selected[d.id]}
                          onChange={(e) => setSelected({ ...selected, [d.id]: e.target.checked })}
                          className="rounded border-zinc-300 text-zinc-950 focus:ring-zinc-950"
                        />
                      </td>
                      <td className="px-4 py-3.5 font-mono font-bold text-zinc-900">
                        {d.businessId || d.dispatchId || d.business_id || (d.id ? `DSP-${d.id.slice(0, 6)}` : "—")}
                      </td>
                      <td className="px-4 py-3.5">
                        {(() => {
                          const isDispatched = String(d.status).toUpperCase() === 'DISPATCHED';
                          const finalInv = isDispatched ? (d.customerInvoiceNumber || d.invoice?.invoiceNumber || d.invoiceNumber) : null;
                          return finalInv ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono font-semibold bg-emerald-50 text-emerald-800 border border-emerald-300 ring-1 ring-emerald-400/20">
                              {finalInv}
                            </span>
                          ) : (
                            <span className="text-zinc-400 text-xs">—</span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3.5 font-medium text-zinc-900">
                        {custName}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="font-medium text-zinc-900">
                          {mergedProductDisplay}
                        </div>
                        <div className="flex gap-1.5 mt-1 flex-wrap">
                          {Array.isArray(d.items) && d.items.map((it, idx) => {
                            const ref = it.inventory?.businessId || it.inventoryBusinessId || it.batchNumber;
                            return ref ? (
                              <span
                                key={idx}
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-medium bg-zinc-100 text-zinc-700 border border-zinc-200"
                              >
                                {ref}
                              </span>
                            ) : null;
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 font-mono text-zinc-600">
                        {formatDate(d.requestedAt || d.createdAt)}
                      </td>
                      <td className="px-4 py-3.5 font-mono text-zinc-600">
                        {formatDate(d.requestedDispatchDate || d.requested_dispatch_date || d.expectedDispatchDate)}
                      </td>
                      <td className="px-4 py-3.5 font-mono text-zinc-600">
                        {formatDate(d.dispatchDate || d.dispatchedAt || d.completedAt)}
                      </td>
                      <td className="px-4 py-3.5">
                        <StatusBadge status={d.status} />
                      </td>
                      <td className="px-4 py-3.5 text-right">
    <Link to={"/warehouse/dispatch/" + d.id} className="inline-flex items-center justify-center rounded-lg bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-zinc-800">View</Link>
  </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function DispatchDetail() {
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const { id } = useParams();
  const queryClient = useQueryClient();

  const approveMutation = useMutation({
    mutationFn: () => API.post(`/dispatch-requests/${id}/approve`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dispatch-detail", id] });
      queryClient.invalidateQueries({ queryKey: ["dispatch-requests"] });
    },
    onError: (err) => alert(err?.response?.data?.message || "Failed to approve dispatch request"),
  });

  const dispatchMutation = useMutation({
    mutationFn: (payload = {}) => API.post(`/dispatch-requests/${id}/dispatch`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dispatch-detail", id] });
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
      queryClient.invalidateQueries({ queryKey: ["dispatch-requests"] });
    },
    onError: (err) => alert(err?.response?.data?.message || "Failed to complete dispatch & invoice"),
  });

  const rejectMutation = useMutation({
    mutationFn: (rejectionReason) => API.post(`/dispatch-requests/${id}/reject`, { rejectionReason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dispatch-detail", id] });
      queryClient.invalidateQueries({ queryKey: ["dispatch-requests"] });
    },
    onError: (err) => alert(err?.response?.data?.message || "Failed to reject dispatch request"),
  });

  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const handleOpenReject = () => {
    setRejectReason("");
    setRejectModalOpen(true);
  };

  const handleConfirmReject = (e) => {
    e?.preventDefault?.();
    if (!rejectReason.trim()) return;
    rejectMutation.mutate(rejectReason.trim(), {
      onSuccess: () => setRejectModalOpen(false),
    });
  };

  const handleRejectPrompt = () => {
    handleOpenReject();
  };
  const { data: d, isLoading } = useQuery({
    queryKey: ["dispatch-detail", id],
    queryFn: () => API.get(`/dispatch-requests/${id}`).then((r) => r.data?.data || r.data),
  });

  const items = d?.items || d?.dispatchItems || [];
  const customerName = d?.customer?.businessName || d?.customerName || "Customer";
  const customerEmail = d?.customer?.email || d?.customerEmail || "—";
  const customerPhone = d?.customer?.phone || d?.customerPhone || "—";
  const deliveryAddress = d?.deliveryAddress || d?.shippingAddress || d?.customer?.address || "—";
  const dispatchLocation = d?.dispatchLocation || d?.location || "—";
    const inventoryRef = items
    .map((i) => i.inventory?.businessId || i.inventoryBusinessId || (i.batchNumber ? `BATCH-${i.batchNumber}` : null) || (i.inventory?.batchNumber ? `BATCH-${i.inventory.batchNumber}` : null) || (i.inventoryId && !i.inventoryId.includes("-") ? `STK-${String(i.inventoryId).padStart(6, "0")}` : null))
    .filter(Boolean)
    .join(", ") || "—";
  const dispatchId = d?.businessId || d?.dispatchId || `DSP-${String(d?.id || "").padStart(6, "0")}`;
  const dispatchInvNo = d?.dispatchInvoiceNumber || d?.dispatchInvoiceNo || "—";
  const invoiceNo = d?.invoiceNumber || d?.invoice?.invoiceNumber || d?.invoiceNo;
  const invoiceStatus = (d?.invoiceStatus || d?.invoice?.status || "UNPAID").toUpperCase();
  const dispatchedDate = d?.dispatchDate || d?.completedAt || d?.actualDispatchDate || (d?.status === "DISPATCHED" ? d?.updatedAt : null);
  const totalQty = items.reduce((sum, it) => sum + (Number(it.requestedQuantity || it.quantity || it.qty || 0)), 0);

  if (isLoading) {
    return <div className="p-8 text-zinc-500 text-xs font-medium">Loading dispatch details...</div>;
  }

  if (!d) {
    return (
      <div className="p-8 text-center text-zinc-500 text-xs">
        Dispatch request not found.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* Top Breadcrumb & Actions Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Link
            to="/warehouse/dispatch"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-500 hover:text-zinc-900 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to dispatches
          </Link>
          <div className="flex items-center gap-2.5 mt-2">
            <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">
              {dispatchId}
            </h1>
            <StatusBadge status={d.status} />
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">
            Customer: <span className="font-semibold text-zinc-800">{customerName}</span>
          </p>
        </div>

        {/* Workflow Actions */}
        <div className="flex items-center gap-2">
          {d.status === "PENDING" && (
            <>
              <button
                type="button"
                onClick={handleRejectPrompt}
                disabled={rejectMutation.isPending}
                className="px-3 py-2 text-xs font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded-lg hover:bg-rose-100 transition disabled:opacity-50"
              >
                {rejectMutation.isPending ? "Rejecting..." : "Reject"}
              </button>
              <button
                type="button"
                onClick={() => approveMutation.mutate()}
                disabled={approveMutation.isPending}
                className="px-4 py-2 text-xs font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 transition disabled:opacity-50 shadow-sm"
              >
                {approveMutation.isPending ? "Approving..." : "Approve Request"}
              </button>
            </>
          )}

          {d.status === "APPROVED" && (
            <button
              type="button"
              onClick={() => setCompleteModalOpen(true)}
              disabled={dispatchMutation.isPending}
              className="px-4 py-2 text-xs font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition disabled:opacity-50 shadow-sm"
            >
              {dispatchMutation.isPending ? "Dispatching..." : "Dispatch & Generate Invoice"}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left Column: Items & Customer Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer & Shipping Info */}
          <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm">
            <h2 className="text-[11px] font-mono tracking-wider text-zinc-400 uppercase font-semibold mb-3">
              CUSTOMER & DISPATCH INFO
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div>
                <span className="text-zinc-400 block mb-0.5">Customer Name</span>
                <span className="font-semibold text-zinc-900">{customerName}</span>
              </div>
              <div>
                <span className="text-zinc-400 block mb-0.5">Contact</span>
                <span className="font-medium text-zinc-700">{customerPhone} • {customerEmail}</span>
              </div>
              <div className="sm:col-span-2">
                <span className="text-zinc-400 block mb-0.5">Dispatch Location</span>
                <span className="font-medium text-zinc-700">{dispatchLocation}</span>
              </div>
              {d?.notes && (
                <div className="sm:col-span-2 pt-2 border-t border-zinc-100">
                  <span className="text-zinc-400 block mb-0.5 font-mono text-[10px] uppercase">Notes / Instructions</span>
                  <p className="font-medium text-zinc-700 italic">{d.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Items Table Card */}
          <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-[11px] font-mono tracking-wider text-zinc-400 uppercase font-semibold">
                  DISPATCH ITEMS
                </h2>
                <p className="text-xs text-zinc-500 font-medium mt-0.5">
                  {items.length} {items.length === 1 ? "item" : "items"} requested
                </p>
              </div>
            </div>

            <div className="overflow-x-auto border-t border-zinc-100 pt-2">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="text-[11px] font-mono text-zinc-400 uppercase border-b border-zinc-100">
                    <th className="pb-3 font-semibold">#</th>
                    <th className="pb-3 font-semibold">PRODUCT NAME</th>
                    <th className="pb-3 font-semibold">BATCH / LOT #</th>
                    <th className="pb-3 font-semibold text-right">REQUESTED QTY</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-50">
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="py-6 text-center text-zinc-400">
                        No items found in this dispatch request.
                      </td>
                    </tr>
                  ) : (
                    items.map((it, idx) => (
                      <tr key={idx} className="hover:bg-zinc-50/50">
                        <td className="py-3 text-zinc-400 font-mono text-[11px]">{idx + 1}</td>
                        <td className="py-3 font-semibold text-zinc-900">
                          {it.productName || it.product?.name || it.name || "—"}
                        </td>
                        <td className="py-3 font-mono text-zinc-600">
                          {it.batchNumber || it.batch?.batchNumber || it.batchNo || "—"}
                        </td>
                        <td className="py-3 font-semibold text-zinc-900 text-right">
                          {it.requestedQuantity || it.quantity || it.qty || 0}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Column: Invoices & Timeline */}
        <div className="space-y-6">
          {/* Invoice Information Card */}
          <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm space-y-4">
            <h2 className="text-[11px] font-mono tracking-wider text-zinc-400 uppercase font-semibold">
              BILLING & INVOICE
            </h2>
            <div className="space-y-3 text-xs">
              <div className="flex items-center justify-between pb-2 border-b border-zinc-100">
                <span className="text-zinc-500">Invoice Number</span>
                <span className="font-mono font-bold text-zinc-900">{invoiceNo || "—"}</span>
              </div>
              <div className="flex items-center justify-between pb-2 border-b border-zinc-100">
                <span className="text-zinc-500">Payment Status</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase border ${
                  invoiceStatus === "PAID"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"
                }`}>
                  {invoiceStatus}
                </span>
              </div>
              <div className="flex items-center justify-between pb-2 border-b border-zinc-100">
                <span className="text-zinc-500">Dispatch Inv Ref</span>
                <span className="font-mono text-zinc-700">{dispatchInvNo}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-500">Inventory Ref</span>
                <span className="font-mono font-semibold text-zinc-900 bg-zinc-100 px-2 py-0.5 rounded text-xs">{inventoryRef}</span>
              </div>
            </div>
          </div>

          {/* Operations Timeline Card */}
          <div className="bg-white border border-zinc-200 rounded-xl p-5 shadow-sm space-y-3">
            <h2 className="text-[11px] font-mono tracking-wider text-zinc-400 uppercase font-semibold">
              DISPATCH TIMELINE
            </h2>
            <div className="space-y-2.5 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-zinc-500">Requested Date</span>
                <span className="font-medium text-zinc-900">{formatDate(d.requestedDate || d.createdAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-500">Expected Dispatch</span>
                <span className="font-medium text-zinc-900">{(d?.requestedDispatchDate || d?.expectedDispatchDate || d?.expectedDate) ? formatDate(d.requestedDispatchDate || d.expectedDispatchDate || d.expectedDate) : "—"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-500">Approved Date</span>
                <span className="font-medium text-zinc-900">{formatDate(d.approvedDate || d.approvedAt)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-zinc-100 pt-2 font-semibold">
                <span className="text-zinc-500 font-medium">Dispatched Date</span>
                <span className="font-semibold text-zinc-900">{dispatchedDate ? (typeof formatDate === "function" ? formatDate(dispatchedDate) : new Date(dispatchedDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })) : "—"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    
      {rejectModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/60 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white rounded-2xl border border-zinc-200 shadow-2xl overflow-hidden p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-semibold text-zinc-900">Reject Dispatch Request</h3>
                <p className="text-xs text-zinc-500 mt-1">Please specify a reason for rejecting this request.</p>
              </div>
              <button
                type="button"
                onClick={() => setRejectModalOpen(false)}
                className="text-zinc-400 hover:text-zinc-600 p-1 rounded-lg hover:bg-zinc-100 transition"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleConfirmReject} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-zinc-700 mb-1.5">
                  Rejection Reason <span className="text-rose-500">*</span>
                </label>
                <textarea
                  rows={3}
                  required
                  placeholder="e.g. Insufficient stock, damaged items, incorrect batch..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full text-xs rounded-lg border border-zinc-200 p-2.5 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none transition resize-none"
                  autoFocus
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-zinc-100">
                <button
                  type="button"
                  onClick={() => setRejectModalOpen(false)}
                  disabled={rejectMutation.isPending}
                  className="px-3.5 py-2 text-xs font-medium text-zinc-600 hover:bg-zinc-100 rounded-lg transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={rejectMutation.isPending || !rejectReason.trim()}
                  className="px-4 py-2 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition disabled:opacity-50 shadow-sm"
                >
                  {rejectMutation.isPending ? "Rejecting..." : "Confirm Rejection"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <CompleteDispatchDialog
        open={completeModalOpen}
        onOpenChange={setCompleteModalOpen}
        onClose={() => setCompleteModalOpen(false)}
        dispatchRequest={d}
        onCompleted={() => {
          setCompleteModalOpen(false);
          queryClient.invalidateQueries({ queryKey: ["dispatch-detail", id] });
          queryClient.invalidateQueries({ queryKey: ["dispatch-requests"] });
        }}
        onComplete={() => {
          setCompleteModalOpen(false);
          queryClient.invalidateQueries({ queryKey: ["dispatch-detail", id] });
          queryClient.invalidateQueries({ queryKey: ["dispatch-requests"] });
        }}
      />
    </div>
  );
}

function CompleteDispatchDialog(props) {
  const { open, onClose, setOpen, dispatchRequest, onCompleted } = props;
  const handleClose = () => {
    if (typeof onClose === 'function') onClose();
    else if (typeof setOpen === 'function') setOpen(false);
    else if (typeof props.onOpenChange === 'function') props.onOpenChange(false);
  };
  const [gstPercentage, setGstPercentage] = useState(18);
  const [chargePresets, setChargePresets] = useState([]);
  const [selectedPresets, setSelectedPresets] = useState([]);
  const [customCharges, setCustomCharges] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setError(null);
      setGstPercentage(18);
      setSelectedPresets([]);
      setCustomCharges([]);
      api.get('/charge-masters')
        .then((res) => {
          const items = res.data?.data || res.data || [];
          setChargePresets(items);
        })
        .catch(() => setChargePresets([]));
    }
  }, [open]);

  if (!open) return null;

  const togglePreset = (preset) => {
    const exists = selectedPresets.find((c) => c.chargeMasterId === preset.id);
    if (exists) {
      setSelectedPresets(selectedPresets.filter((c) => c.chargeMasterId !== preset.id));
    } else {
      setSelectedPresets([
        ...selectedPresets,
        {
          chargeMasterId: preset.id,
          name: preset.name,
          amount: preset.defaultAmount || 0,
        },
      ]);
    }
  };

  const handlePresetAmountChange = (chargeMasterId, amount) => {
    setSelectedPresets(
      selectedPresets.map((c) =>
        c.chargeMasterId === chargeMasterId ? { ...c, amount: Number(amount) || 0 } : c
      )
    );
  };

  const addCustomCharge = () => {
    setCustomCharges([...customCharges, { id: Date.now(), name: '', amount: 0 }]);
  };

  const removeCustomCharge = (id) => {
    setCustomCharges(customCharges.filter((c) => c.id !== id));
  };

  const updateCustomCharge = (id, field, value) => {
    setCustomCharges(
      customCharges.map((c) =>
        c.id === id ? { ...c, [field]: field === 'amount' ? Number(value) || 0 : value } : c
      )
    );
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    setError(null);
    try {
      const additionalCharges = [
        ...selectedPresets.map((c) => ({
          chargeMasterId: c.chargeMasterId,
          name: c.name,
          amount: Number(c.amount) || 0,
        })),
        ...customCharges
          .filter((c) => c.name.trim() !== '')
          .map((c) => ({
            name: c.name.trim(),
            amount: Number(c.amount) || 0,
          })),
      ];

      const payload = {
        gstPercentage: Number(gstPercentage) || 0,
        additionalCharges,
      };

      const res = await api.post(`/dispatch-requests/${dispatchRequest.id}/complete`, payload);
      const data = res.data?.data || res.data;
      if (typeof onCompleted === "function") onCompleted(data);
      if (typeof props.onComplete === "function") props.onComplete(data);
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to complete dispatch');
    } finally {
      setSubmitting(false);
    }
  };

  const totalAdditional =
    selectedPresets.reduce((sum, c) => sum + (Number(c.amount) || 0), 0) +
    customCharges.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl border border-zinc-200 my-8">
        <div className="flex items-center justify-between pb-3 border-b border-zinc-100">
          <div>
            <h2 className="text-lg font-bold text-zinc-900">Dispatch & bill</h2>
            {dispatchRequest?.businessId && (
              <p className="text-xs text-zinc-500 font-mono mt-0.5">Ref: {dispatchRequest.businessId}</p>
            )}
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 transition"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mt-3 rounded-lg bg-red-50 p-3 text-xs text-red-600 border border-red-200">
            {error}
          </div>
        )}

        <div className="mt-4 space-y-4">
          

          <div>
            <label className="block text-xs font-semibold text-zinc-700 mb-1.5">GST %</label>
            <input
              type="number"
              min="0"
              max="100"
              value={gstPercentage}
              onChange={(e) => setGstPercentage(e.target.value)}
              className="w-28 rounded-lg border border-zinc-300 px-3 py-2 text-sm font-mono text-zinc-900 focus:border-zinc-900 focus:outline-none"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-zinc-700">Additional charges</label>
              <button
                type="button"
                onClick={addCustomCharge}
                className="flex items-center gap-1 rounded-lg border border-zinc-200 px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 transition"
              >
                <span>+</span> Add custom
              </button>
            </div>

            {chargePresets.length > 0 && (
              <div className="space-y-2 mb-3 max-h-40 overflow-y-auto pr-1">
                {chargePresets.map((preset) => {
                  const isChecked = selectedPresets.some((c) => c.chargeMasterId === preset.id);
                  const active = selectedPresets.find((c) => c.chargeMasterId === preset.id);
                  return (
                    <div
                      key={preset.id}
                      className="flex items-center justify-between p-2.5 rounded-lg border border-zinc-200 hover:bg-zinc-50/50 transition text-xs"
                    >
                      <label className="flex items-center gap-2.5 cursor-pointer font-medium text-zinc-800">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => togglePreset(preset)}
                          className="h-4 w-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
                        />
                        <span>{preset.name}</span>
                      </label>
                      {isChecked && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-zinc-400 text-xs">₹</span>
                          <input
                            type="number"
                            value={active?.amount ?? preset.defaultAmount ?? 0}
                            onChange={(e) => handlePresetAmountChange(preset.id, e.target.value)}
                            className="w-24 rounded-md border border-zinc-300 px-2 py-1 text-right text-xs font-mono focus:border-zinc-900 focus:outline-none"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {customCharges.length > 0 && (
              <div className="space-y-2">
                {customCharges.map((charge) => (
                  <div key={charge.id} className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Charge name"
                      value={charge.name}
                      onChange={(e) => updateCustomCharge(charge.id, 'name', e.target.value)}
                      className="flex-1 rounded-lg border border-zinc-300 px-3 py-2 text-xs focus:border-zinc-900 focus:outline-none"
                    />
                    <div className="relative w-28">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">₹</span>
                      <input
                        type="number"
                        placeholder="Amount"
                        value={charge.amount || ''}
                        onChange={(e) => updateCustomCharge(charge.id, 'amount', e.target.value)}
                        className="w-full rounded-lg border border-zinc-300 pl-6 pr-2 py-2 text-right text-xs font-mono focus:border-zinc-900 focus:outline-none"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeCustomCharge(charge.id)}
                      className="rounded-lg p-2 text-zinc-400 hover:bg-zinc-100 hover:text-red-600 transition text-sm"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {totalAdditional > 0 && (
            <div className="flex justify-between items-center bg-zinc-50 p-3 rounded-lg border border-zinc-200 text-xs font-medium">
              <span className="text-zinc-600">Total Additional Charges:</span>
              <span className="font-mono font-bold text-zinc-900">₹{totalAdditional.toLocaleString('en-IN')}</span>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2 border-t border-zinc-100 pt-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="rounded-lg border border-zinc-300 px-4 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="rounded-lg bg-zinc-900 px-4 py-2 text-xs font-semibold text-white hover:bg-zinc-800 transition disabled:opacity-50"
          >
            {submitting ? 'Processing...' : 'Dispatch & bill'}
          </button>
        </div>
      </div>
    </div>
  );
}
